import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useFilterTemplateMutations } from '~/hooks/use-filter-templates';
import { filterTemplateSchema, EMPTY_FILTER_TEMPLATE, type FilterTemplateFormValues } from '~/lib/schemas/filter-template';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: FilterTemplateFormValues | null;
}

/** Add / edit a filter template (Πρότυπο Φίλτρων). */
export function FilterTemplateEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useFilterTemplateMutations();
	const form = useForm<FilterTemplateFormValues>({
		resolver: zodResolver(filterTemplateSchema) as Resolver<FilterTemplateFormValues>,
		defaultValues: initial ?? EMPTY_FILTER_TEMPLATE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_FILTER_TEMPLATE);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('catalog.filterTemplates.editTitle') : t('catalog.filterTemplates.addTitle')}</DialogTitle>
					<DialogDescription>{t('catalog.filterTemplates.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('catalog.filterTemplates.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="ft-iv">{t('catalog.filterTemplates.columns.visible')}</Label>
							<Switch id="ft-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
						</div>
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="ft-default">{t('catalog.filterTemplates.columns.isDefault')}</Label>
							<Switch
								id="ft-default"
								checked={watch('IsDefault')}
								onCheckedChange={next => setValue('IsDefault', next, { shouldDirty: true })}
							/>
						</div>
						<div className="col-span-2 flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="ft-mf-default">{t('catalog.filterTemplates.columns.isManufacturerDefault')}</Label>
							<Switch
								id="ft-mf-default"
								checked={watch('IsManufacturerDefault')}
								onCheckedChange={next => setValue('IsManufacturerDefault', next, { shouldDirty: true })}
							/>
						</div>
					</div>
					<FormField label={t('catalog.filterTemplates.columns.sort')} error={formState.errors.si?.message}>
						<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
					</FormField>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
